import { Api } from './api/Api';
import { EnumerateCreditsResponseModel } from './api/data-contracts';
import { HttpClient, NoDataError, RequestParams } from './api/http-client';
import {
  Banner,
  BnplResponse,
  ConstructorSales,
  CourierIntervalsVersion,
  Esia,
  ExtendedBasketRequestModel,
  ExtendedBasketResponseModel,
  ExtendedOrderResponseModel,
  ExtendedPaymentsGetResponse,
  ExtendedRequestModel6,
  ExtendedResponseService,
  FlocktoryTariff,
  Helpers,
  Mapper,
  OrderStatus,
  PersonalConsent,
  QPInterface,
  QpKey,
  RecallSettings,
  RecommendsItemResponseModel,
  Sbp,
  stringToArray,
  stringToObject,
  TermsOfSale,
  TradeIn,
} from './mapping';
import {
  enrichIntervalsWithVersion,
  handleRoutesV2Request,
  joinCourierRequests,
} from './mapping/routes';

export class CartApi extends Api {
  helpers: Helpers;

  mapper: Mapper;

  constructor(client: HttpClient, mapper: Mapper, helpers: Helpers) {
    super(client);

    this.helpers = helpers;
    this.mapper = mapper;
  }

  joinedBasketGetDeliveryRoutes = (...args: Parameters<Api['basketGetDeliveryRoutesV2']>) =>
    Api.joinRequests([
      super
        .basketGetDeliveryRoutes(args[0])
        .then(enrichIntervalsWithVersion(CourierIntervalsVersion.v1)),
      super
        .basketGetDeliveryRoutesV2(...args)
        .then(enrichIntervalsWithVersion(CourierIntervalsVersion.v2)),
    ]).then(joinCourierRequests);

  basketGetOnlyDeliveryRoutesV2 = (...args: Parameters<Api['basketGetDeliveryRoutesV2']>) =>
    super
      .basketGetDeliveryRoutesV2(...args)
      .then(enrichIntervalsWithVersion(CourierIntervalsVersion.v2))
      .then(handleRoutesV2Request);

  getQPShopBasket = () =>
    super
      .utilityGetSiteText(
        { key: QpKey.ShopBasket },
        {
          delay: 1000,
          errors: [NoDataError],
          retries: Infinity,
        },
      )
      .then((data) => stringToObject<QPInterface.QPBasketShop>(data));

  getQPShopBasketTexts = () =>
    super
      .utilityGetSiteText(
        { key: QpKey.ShopBasketTexts },
        {
          delay: 1000,
          errors: [NoDataError],
          retries: 1,
        },
      )
      .then((data) => stringToObject<QPInterface.QPTexts>(data));

  getQPPersonalConsent = () =>
    super
      .utilityGetSiteText(
        { key: QpKey.ShopBasketPersonalDataConsent },
        {
          delay: 1000,
          errors: [NoDataError],
          retries: 3,
        },
      )
      .then((data) => stringToObject<PersonalConsent>(data));

  getQPTermsOfSale = () =>
    super
      .utilityGetSiteText(
        { key: QpKey.ShopBasketTermsOfSale },
        {
          delay: 1000,
          errors: [NoDataError],
          retries: 3,
        },
      )
      .then((data) => stringToObject<TermsOfSale>(data));

  getQPConstructorSales = () =>
    super
      .utilityGetSiteText({ key: QpKey.ShopBasketConstructorSales })
      .then((data) => stringToObject<ConstructorSales>(data));

  getQPBanner = () =>
    super
      .utilityGetSiteText({ key: QpKey.ShopBasketBanner })
      .then((data) => stringToObject<Banner>(data));

  getQPBnplTexts = () =>
    super
      .utilityGetSiteText({ key: QpKey.BnplTexts })
      .then((data) => stringToObject<QPInterface.QPBnpl>(data));

  getQPSberTexts = () =>
    super
      .utilityGetSiteText({ key: QpKey.SberInstallment })
      .then((data) => stringToObject<QPInterface.QPSberInstallment>(data));

  getQPOnlineDelivery = () =>
    super
      .utilityGetSiteText({ key: QpKey.BasketOnlineDelivery })
      .then((data) => stringToObject<Record<string, unknown>>(data));

  getQPEsia = () =>
    super
      .utilityGetSiteText({ key: QpKey.BasketEsiaTexts })
      .then((data) => stringToObject<Esia>(data));

  getQPFlocktoryTariff = () =>
    super
      .utilityGetSiteText({ key: QpKey.ShopBasketFlocktoryTarrifPrefix })
      .then((data) => stringToObject<FlocktoryTariff>(data));

  getQPTradeIn = () =>
    super
      .utilityGetSiteText({ key: QpKey.ShopTradeInContentTexts })
      .then((data) => stringToObject<TradeIn>(data));

  getQPRecall = () =>
    super
      .utilityGetSiteText({ key: QpKey.ShopBasketRecall })
      .then((data) => stringToObject<RecallSettings>(data));

  getQPMobileId = () =>
    super
      .utilityGetSiteText({ key: QpKey.BasketMobileIdTexts })
      .then((data) => stringToObject<Record<string, unknown>>(data));

  getQPSpbBanks = () =>
    super
      .utilityGetSiteText({ key: QpKey.ShopBasketSpbBanks })
      .then((data) => stringToObject<Sbp>(data));

  getQPStatusPageSettings = () =>
    super
      .utilityGetSiteText({ key: QpKey.ShopBasketStatusPageSettings })
      .then((data) => stringToArray<OrderStatus>(data));

  composeCaptchaImage(key: string, weight: number, height: number) {
    const uri = this.http.instance.getUri();
    const baseUrl = uri && uri !== '/' ? uri : '';

    return `${baseUrl}${this.composeCaptchaGetImageUrl()}?w=${weight}&h=${height}&key=${key}`;
  }

  composePaymentCardFrameUrl(paymentId: string, returnUrl: string) {
    const uri = this.http.instance.getUri();
    const baseUrl = uri && uri !== '/' ? uri : '';

    return `${baseUrl}${this.composePaymentGetCardViewUrl()}?paymentId=${paymentId}&returnUrl=${returnUrl}`;
  }

  /**
   * !!! Расширение типов !!!
   * Для некоторых полей на бэке нет типов(и не будет)
   */
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  orderGetPayments(...args: Parameters<Api['orderGetPayments']>) {
    return super.orderGetPayments<ExtendedPaymentsGetResponse>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketGetBnpl(...args: Parameters<Api['basketGetBnpl']>) {
    return super.basketGetBnpl<BnplResponse>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketGetBasket(...args: Parameters<Api['basketGetBasket']>) {
    return super.basketGetBasket<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketAddItems(...args: Parameters<Api['basketAddItems']>) {
    return super.basketAddItems<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketUpdateItem(...args: Parameters<Api['basketUpdateItem']>) {
    return super.basketUpdateItem<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketDeleteItem(...args: Parameters<Api['basketDeleteItem']>) {
    return super.basketDeleteItem<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketCreateBasket(request: ExtendedBasketRequestModel, params: RequestParams = {}) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return super.basketCreateBasket<ExtendedBasketResponseModel>(request, params);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketCopyOrderBasket(...args: Parameters<Api['basketCopyOrderBasket']>) {
    return super.basketCopyOrderBasket<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketUpdateRegion(...args: Parameters<Api['basketUpdateRegion']>) {
    return super.basketUpdateRegion<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketDeletePromoCode(...args: Parameters<Api['basketDeletePromoCode']>) {
    return super.basketDeletePromoCode<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketApplyPromoCode(...args: Parameters<Api['basketApplyPromoCode']>) {
    return super.basketApplyPromoCode<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketMergeItems(...args: Parameters<Api['basketMergeItems']>) {
    return super.basketMergeItems<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketMergeBaskets(...args: Parameters<Api['basketMergeBaskets']>) {
    return super.basketMergeBaskets<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketAddItems2(...args: Parameters<Api['basketAddItems2']>) {
    return super.basketAddItems2<ExtendedBasketResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  orderGetOrder(...args: Parameters<Api['orderGetOrder']>) {
    return super.orderGetOrder<ExtendedOrderResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  orderCompleteOrder(...args: Parameters<Api['orderCompleteOrder']>) {
    return super.orderCompleteOrder<ExtendedOrderResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  orderCompleteDeferredOrder(...args: Parameters<Api['orderCompleteDeferredOrder']>) {
    return super.orderCompleteDeferredOrder<ExtendedOrderResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketUpdateDelivery(...args: Parameters<Api['basketUpdateDelivery']>) {
    // @ts-expect-error баг на бэке, возвращается не верный тип
    return super.basketUpdateDelivery<ExtendedOrderResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  upSaleEnumerateRecommends(...args: Parameters<Api['upSaleEnumerateRecommends']>) {
    // FIXME временно указан примерный тип, на бэке сделают другой
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return super.upSaleEnumerateRecommends<RecommendsItemResponseModel[]>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketUpdateClientInfo(
    id: Parameters<Api['basketUpdateClientInfo']>[0],
    request: ExtendedRequestModel6,
  ) {
    return super.basketUpdateClientInfo(id, request);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  orderGetDeferredOrder(...args: Parameters<Api['orderGetDeferredOrder']>) {
    return super.orderGetDeferredOrder<ExtendedOrderResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  creditsEnumerateCredits(...args: Parameters<Api['creditsEnumerateCredits']>) {
    // FIXME: Необходимо чтобы бэк поменял тип у респонса на EnumerateCreditsResponseModel, так как сейчас там массив
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return super.creditsEnumerateCredits<EnumerateCreditsResponseModel>(...args);
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  basketGetItemServiceList(...args: Parameters<Api['basketGetItemServiceList']>) {
    // FIXME: Необходимо чтобы бэк поменял тип у респонса на ResponseService[], так как сейчас там объект массивов
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return super.basketGetItemServiceList<ExtendedResponseService[]>(...args);
  }
}
